import React, { useMemo, useState } from 'react';
import { View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { AppText } from '@/components/ui/AppText';
import { Card } from '@/components/ui/Card';
import { Segmented } from '@/components/ui/Segmented';
import { useThemePalette } from '@/config/theme';
import type { MatchStatsFull, MatchXgStats, XgPhase, XgStatGroup } from '@/lib/data/provider';
import { localizeDigits } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';

/** A row spec: which stat key in a group to read + its localized label + format. */
interface RowSpec {
  key: string;
  labelKey: string;
  /** Percentage stats render with a trailing % and round to whole numbers. */
  percent?: boolean;
  /** xG-style decimals (one place) instead of integers. */
  decimal?: boolean;
}

interface GroupSpec {
  /** The stats_full group key. */
  group: keyof MatchStatsFull;
  titleKey: string;
  rows: RowSpec[];
}

/**
 * A curated, FotMob-style subset of the most meaningful stats per group (we
 * deliberately don't dump every raw key). Each row maps a real stat key to a
 * localized label; absent keys/groups are skipped at render time.
 */
const GROUPS: GroupSpec[] = [
  {
    group: 'shots',
    titleKey: 'match.groupShots',
    rows: [
      { key: 'total_shots', labelKey: 'match.statTotalShots' },
      { key: 'shots_on_target', labelKey: 'match.shotsOnTarget' },
      { key: 'shots_inside_box', labelKey: 'match.statShotsInsideBox' },
      { key: 'shots_outside_box', labelKey: 'match.statShotsOutsideBox' },
      { key: 'blocked_shots', labelKey: 'match.statBlockedShots' },
      { key: 'hit_woodwork', labelKey: 'match.statHitWoodwork' },
    ],
  },
  {
    group: 'attack',
    titleKey: 'match.groupAttack',
    rows: [
      { key: 'big_chances', labelKey: 'match.statBigChances' },
      { key: 'touches_in_box', labelKey: 'match.statTouchesInBox' },
      { key: 'final_third_entries', labelKey: 'match.statFinalThirdEntries' },
    ],
  },
  {
    group: 'passes',
    titleKey: 'match.groupPasses',
    rows: [
      { key: 'accurate_passes', labelKey: 'match.statAccuratePasses' },
      { key: 'accurate_crosses', labelKey: 'match.statCrosses' },
      { key: 'accurate_long_balls', labelKey: 'match.statLongBalls' },
      { key: 'final_third_entries', labelKey: 'match.statFinalThirdEntries' },
      { key: 'throw_ins', labelKey: 'match.statThrowIns' },
    ],
  },
  {
    group: 'duels',
    titleKey: 'match.groupDuels',
    rows: [
      { key: 'duels_won_percentage', labelKey: 'match.statDuelsWon', percent: true },
      { key: 'aerial_duels_percentage', labelKey: 'match.statAerialDuels', percent: true },
      { key: 'ground_duels_percentage', labelKey: 'match.statGroundDuels', percent: true },
      { key: 'dribbles_percentage', labelKey: 'match.statDribbles', percent: true },
      { key: 'dispossessed', labelKey: 'match.statDispossessed' },
    ],
  },
  {
    group: 'defending',
    titleKey: 'match.groupDefending',
    rows: [
      { key: 'tackles', labelKey: 'match.statTackles' },
      { key: 'tackles_won_percentage', labelKey: 'match.statTacklesWon', percent: true },
      { key: 'interceptions', labelKey: 'match.statInterceptions' },
      { key: 'clearances', labelKey: 'match.statClearances' },
      { key: 'ball_recoveries', labelKey: 'match.statRecoveries' },
    ],
  },
  {
    group: 'goalkeeping',
    titleKey: 'match.groupGoalkeeping',
    rows: [
      { key: 'saves', labelKey: 'match.statSaves' },
      { key: 'goals_prevented', labelKey: 'match.statGoalsPrevented', decimal: true },
    ],
  },
];

/** Read a stat's home/away pair for the chosen phase; null when the key is absent. */
function readPair(
  group: XgStatGroup | undefined,
  key: string,
  phase: XgPhase,
): { home: number; away: number } | null {
  const split = group?.[key];
  if (!split) return null;
  const pair = split[phase];
  if (!pair) return null;
  if (typeof pair.home !== 'number' || typeof pair.away !== 'number') return null;
  return { home: pair.home, away: pair.away };
}

/**
 * The home (volt / cyan) value pill, anchored to the start of the row. The
 * leading side reads brighter + heavier and gets a translucent accent fill with
 * a hairline border; the trailing side is a quieter ghost pill so the two teams
 * stay visually distinct even though volt and live share the cyan family.
 */
function StatPill({
  text,
  side,
  leads,
}: {
  text: string;
  side: 'home' | 'away';
  leads: boolean;
}) {
  const palette = useThemePalette();
  const accent = side === 'home' ? palette.volt : palette.teamAway;
  // Leading pill: solid translucent accent fill + accent border + bright text.
  // Trailing pill: barely-there fill + hairline border + muted text.
  const backgroundColor = leads ? `${accent}26` : palette.inset;
  const borderColor = leads ? accent : palette.line;
  return (
    <View
      className="min-w-[44px] items-center rounded-full border px-2.5 py-1"
      style={{ backgroundColor, borderColor }}
    >
      <AppText
        variant="caption"
        weight="bold"
        tabular
        style={{ color: leads ? accent : palette.muted }}
      >
        {text}
      </AppText>
    </View>
  );
}

/**
 * One FotMob-style stat row: the home value in a volt pill at the start, the
 * localized label centred, the away value in a live pill at the end, and a thin
 * dual comparison bar beneath (home share vs away share). Laid out LTR so home
 * is always the leading half; the outer flex mirrors naturally in RTL.
 */
function StatRow({
  label,
  home,
  away,
  percent,
  decimal,
}: {
  label: string;
  home: number;
  away: number;
  percent?: boolean;
  decimal?: boolean;
}) {
  const palette = useThemePalette();
  const { arabicNumerals } = useLocale();
  const total = home + away;
  const homeShare = total > 0 ? home / total : 0.5;
  const homeLeads = home >= away;
  const awayLeads = away >= home;
  const format = (value: number) => {
    const text = decimal ? (Math.round(value * 10) / 10).toFixed(1) : String(Math.round(value));
    return `${localizeDigits(text, arabicNumerals)}${percent ? '%' : ''}`;
  };

  return (
    <View className="gap-2 px-4 py-2.5">
      <View className="flex-row items-center justify-between gap-3" style={{ direction: 'ltr' }}>
        <StatPill text={format(home)} side="home" leads={homeLeads} />
        <AppText
          variant="micro"
          weight="medium"
          className="flex-1 text-center text-soft"
          numberOfLines={1}
        >
          {label}
        </AppText>
        <StatPill text={format(away)} side="away" leads={awayLeads} />
      </View>
      <View
        className="h-1.5 flex-row overflow-hidden rounded-full bg-surface-2"
        style={{ direction: 'ltr' }}
      >
        <View
          style={{
            flex: Math.max(homeShare, 0.02),
            backgroundColor: palette.volt,
            opacity: homeLeads ? 1 : 0.55,
          }}
        />
        <View style={{ width: 1.5, backgroundColor: palette.deep }} />
        <View
          style={{
            flex: Math.max(1 - homeShare, 0.02),
            backgroundColor: palette.teamAway,
            opacity: awayLeads ? 0.85 : 0.4,
          }}
        />
      </View>
    </View>
  );
}

/** Section header for a stat group. */
function GroupHeader({ title }: { title: string }) {
  return (
    <View className="border-b border-line bg-surface-2/40 px-4 py-2">
      <AppText variant="micro" weight="bold" className="uppercase tracking-wide text-soft">
        {title}
      </AppText>
    </View>
  );
}

/**
 * The complete grouped stats list (FotMob-style): Possession + xG up top (from
 * the overview), then Shots / Attack / Passing / Duels / Defending /
 * Goalkeeping sections, each a set of pill-flanked comparison rows for the
 * chosen phase. An All / 1st half / 2nd half toggle switches which split every
 * row reads. A group (or row) is skipped when its data is absent.
 */
export function XgFullStats({ stats }: { stats: MatchXgStats }) {
  const { t } = useTranslation();
  const [phase, setPhase] = useState<XgPhase>('all');
  const full = stats.stats_full;

  // Possession + xG always head the block, derived from the headline columns +
  // the overview group for the per-half splits where available.
  const overview = full?.overview;
  const topRows = useMemo(() => {
    const possession = readPair(overview, 'ball_possession', phase) ?? {
      home: stats.home_possession,
      away: stats.away_possession,
    };
    const xg = readPair(overview, 'expected_goals', phase) ?? {
      home: stats.home_xg,
      away: stats.away_xg,
    };
    return { possession, xg };
  }, [overview, phase, stats]);

  // Pre-compute the visible rows per group for the chosen phase (skip empties).
  const groups = useMemo(() => {
    if (!full) return [];
    return GROUPS.map((spec) => {
      const group = full[spec.group];
      const rows = spec.rows
        .map((row) => {
          const pair = readPair(group, row.key, phase);
          return pair ? { ...row, ...pair } : null;
        })
        .filter((row): row is RowSpec & { home: number; away: number } => row !== null);
      return { spec, rows };
    }).filter((entry) => entry.rows.length > 0);
  }, [full, phase]);

  return (
    <View className="gap-3">
      <Segmented
        options={[
          { value: 'all', label: t('match.phaseAll') },
          { value: 'first_half', label: t('match.phaseFirstHalf') },
          { value: 'second_half', label: t('match.phaseSecondHalf') },
        ]}
        value={phase}
        onChange={setPhase}
      />
      <Card className="overflow-hidden py-1">
        <StatRow
          label={t('match.possession')}
          home={topRows.possession.home}
          away={topRows.possession.away}
          percent
        />
        <StatRow label={t('match.xg')} home={topRows.xg.home} away={topRows.xg.away} decimal />
        {groups.map((entry) => (
          <View key={entry.spec.group}>
            <GroupHeader title={t(entry.spec.titleKey)} />
            {entry.rows.map((row) => (
              <StatRow
                key={row.key}
                label={t(row.labelKey)}
                home={row.home}
                away={row.away}
                percent={row.percent}
                decimal={row.decimal}
              />
            ))}
          </View>
        ))}
      </Card>
    </View>
  );
}
